
import type {
  PeriodRuleItemData,
  ApplyChannelData,
  ApplyProductData,
  CalcPlanListData,
  FundListDataType,
  LoanUsageOption,
  RemoteContract,
  SubmitApplyResultData,
  CheckBankCardCanUse,
  OrderQueryRealTime,
  OrderCheck,
  CardBindTypeEnum,
  ProductCapitalItem,
  UserSceneEnum,
  LoanApplyMarketReqType,
} from '@/api/apply-product/types';
import type { CouponRolePo } from '@/api/coupon/types';
import type { ContractItem } from '@/ContractConfig';
import type { ABResEnum } from '@/enums/abExperiment';

import type { Commodity, Contract } from '@/api/contract/types';
import type {
  AutrakyProcessExpEnum, RightCheckResultType, RightSummaryType, SuperCardSummaryBackType,
} from '@/api/rights/types';
import { EquitySelectedEnum } from '@/enums/rights';

// 字段校验方法
export type ValidateFunction = () => string;

// 期限下拉列表
export interface PeriodOption extends PeriodRuleItemData {
  value: string | number;
  label: string;
  unitName: string;
}

// 提现收款银行卡
export interface LoanBankCard {
  bankName: string;
  bankNo: string;
  bankIcon: string;
  bankId: string;
}

// 渠道对地理位置信息的要求类型
export interface LoanConfigCity {
  gpsLoanDepend: LoanGpsEnum;
}

/** 营销额度状态 */
export interface MarketingAmountStateType {
  /** 营销系数 用于计算展示潜力额度 */
  marketingCoefficient: number | null;
}

/** 优惠模块状态 */
export interface DiscountModuleStateType {
  /** 优惠模块优化实验结果 */
  discountModuleExperimentRes: ABResEnum | null;
  /** 优惠模块临时额度优化实验结果 */
  discountModuleTempAmountExperimentRes: ABResEnum | null;
}

/** 优惠券折扣率实验结果 */
export interface CouponDiscountRateStateType {
  /** 优惠券折扣率实验结果 */
  couponDiscountRateExperimentRes: ABResEnum | null;
}

/** 提现页面优化实验状态类型 */
export interface ApplyOptimizeStateType {
  /** 提现页面优化实验结果 */
  applyOptimizeExperimentRes: ABResEnum | null;
  /** 提现页面优化子实验结果 */
  applyOptimizeSubExperimentRes: ABResEnum | null;
}


/** 满额提现券信息 */
export interface FullAmountCouponType {
  /** 满额提现券金额 */
  amount: string;
  /** 是否需要选中满额提现券 */
  needSelect: boolean;
  /** 实验结果 */
  experimentResult: ABResEnum | undefined;
}

export interface StateType {
  // 公告信息
  notice: string;

  // 可用渠道列表以及当前选中的渠道信息 以及 动态利率的渠道
  channel: ApplyChannelData | undefined;
  channelList: ApplyChannelData[];

  // 进入实验组1且处于额度隔离优化交互
  optimizationNoAvailableProduct: boolean;

  // 用户场景
  scene: UserSceneEnum;
  /** 用户场景交互配置 */
  sceneConfig: UserSceneConfig;

  // 选中渠道的产品信息（包括额度），并缓存相关信息（查询产品信息可能需要调用三方接口，一次性返回有所产品信息性能较差，比如：动态期限列表等）
  product: ApplyProductData | undefined;
  productList: Dictionary<ApplyProductData>;

  // 中断提现金额
  brokenAmount: string;
  /** 中断提现订单是否允许取消 */
  canCancel: boolean;
  /** 中断提现订单渠道 id */
  brokenProductId: string;
  // 申请提现金额、期限
  amount: string;
  maxAmount: string;
  period: PeriodOption | undefined;

  // 动态计算的期限列表
  periodList: PeriodOption[];

  // 多资方借款试算列表
  fundListData: FundListDataType[] | undefined;

  /** 当前选择的借款试算资方index */
  currentCheckFundIndex: number,

  // 单条借款试算结果
  planListData: CalcPlanListData | undefined;

  /** 还款计划-是否查询中 */
  loadingCalcRepayPlans: boolean;

  // 收款银行卡账户
  bankCard: LoanBankCard | undefined;

  // 可用优惠券列表以及选中的优惠券信息
  coupon: CouponRolePo | undefined;
  couponList: CouponRolePo[];
  /** 满额提现券信息 */
  fullAmountCoupon: FullAmountCouponType;

  // 借款用途列表以及选中项
  usage: LoanUsageOption | undefined;
  usageList: LoanUsageOption[];

  // 协议列表
  isDynamicContracts: boolean;
  contracts: (ContractItem | RemoteContract | Commodity)[];

  /** 8180授信协议，提现时，授信8180场景使用 */
  creditContracts: Contract[];

  // 是否需要交易密码以及用户输入的交易密码
  needTradePasswd: boolean;
  tradePasswdToken: string;

  // 下单请求返回的数据
  submitResult: SubmitApplyResultData | undefined;

  // 支付宝签约状态
  iSalipaySignSuccess: boolean;

  loanConfigCity: LoanConfigCity | undefined;

  // 银行卡是否可用
  isBankCardUsed: CheckBankCardCanUse;
  // 不选择优惠券
  isCouponVisible: boolean;

  // 是否需要补充资料
  needSupplyMaterials: boolean;
  // 还款方式
  repaymentModel: string;
  // 无资金路由
  replanResultErrMsg: boolean;
  // abtest测试
  channelGrayTest: number;
  /** 获取提现渠道列表返回的需透传内容 */
  channelExtendObj: Record<string, unknown>;
  // 可提现渠道总金额
  totalAvailableAmount: number,
  checkType: string,
  // 多还款配置方式
  multiChannelRepayment: string
  // 验证码
  smsCode: string
  lastApplyResult: string
  // loading展示
  isVisibleLoading: boolean
  checkId: string
  queryRealTimeStatus: OrderQueryRealTime | null
  orderCheckListDxm: OrderCheck | null
  /** 试算接口调用时存储的时间戳 */
  calcRepayPlansTime: number,
  /** h5活体成功以后 取值 */
  h5BioSuc: boolean,
  /** couponId 优惠券列表 跳转到提现页场景，默认选中的优惠券 id，供优惠券列表匹配默认选择优惠券使用 */
  couponId: string,
  /** externalCouponId 渠道优惠券列表 跳转到提现页场景，默认选中的渠道优惠券 id，供优惠券列表匹配默认选择优惠券使用 */
  externalCouponId?: string,
  /** 支付类型 */
  payType: CardBindTypeEnum,
  /** 权益校验数据 */
  rightCheckStatus: RightCheckResultType | null,
  /** 是否通联二次绑卡校验 */
  allinPayCheck: boolean,
  /** 双融担模式，用户选择权益对应的资方信息(P36无权益，P24有权益) */
  selectRightInCapital: ProductCapitalItem | null,
  /** 双融担tab(资方)切换，对应银行卡是否变更 */
  selectCapitalChangedForBank: boolean;
  /** 防诈骗配置 */
  antiFraudConfig: AntiFraudConfigType | null,
  /** 是否显示 怎么还 模块，默认不显示 */
  showHowToRepay: boolean;
  /** 是否按期还款 默认 false */
  repayOnSchedule: boolean;
  /** 是否需要加载资源弹窗，首次计算完提现期数, 期数是提现金额计算完毕后计算的 */
  needLoadingAdvPopup: boolean;
  /** 非自营提现 展示升级入口 用户是否勾选, 默认 false */
  isUpgradeChecked: boolean;
  /** 是否需要重新初始化权益卡片状态 */
  shouldInitSpeedState: boolean;
  /** 是否预授信召回挽留流程 */
  recallPreCredit: boolean;
  /** 自营流程实验结果 */
  autrakyProcessExpRes: AutrakyProcessExpEnum;
  /** 是否24+单通道可换普通通道场景 */
  is24NeedRightAndNormalIs36: boolean;
  /** 标记自营产品是否需要购买臻享卡的状态 */
  autrakyZhenxiangCardState: {
    clickedSubmitProductIds: string[],
    autrakyNeedBuyZhenxiangCard: boolean,
  };
  /** 提额入口相关状态 */
  tempAmountEntry: TempAmountEntryStateType;
}

/** 提额入口状态 */
export interface TempAmountEntryStateType {
  /**
   * 是否展示提额入口
   * - undefined: 未请求过接口
   * - false: 已请求，不展示（包括接口异常）
   * - true: 已请求，展示入口
   */
  showEntry: boolean | undefined;
  /** 提额入口是否已点击（用于控制入口隐藏） */
  clicked: boolean;
  /** 是否为主动提额场景（用于判断轮询成功后是否弹窗） */
  applying: boolean;
  /** 提额成功弹窗展示 */
  showSuccessPopup: boolean;
}

export interface ApplyProductStateType extends StateType {
  marketingAmount: MarketingAmountStateType;
}

// 倒计时等待类型
export enum WaitCountNumberType {
  /**
   * 4s倒计时
   * */
  wait = 4,
  /**
   * 33s倒计时
   * */
  loanWait = 33
}
/**
 * 借款试算失败
 * */
export enum ReplanResultType {
  REPLAN_FAILED = 'FAILED'
}

// 文案
export enum DialogTextEnum {
  WE_KNOW = '我知道了',
  GET_QUOTA = '获得额度',
  SURE = '确定',
  CHANEG_PRODUCT = '更换放款机构',
  TITLE = '抱歉',
  UNAVAILABLE = '不可用'
}

// gps依赖枚举
export enum LoanGpsEnum {
  M = 'M', // 必选
  O = 'O', // 可选
  N = 'N', // 无需
}

export enum SpeedRightsType {
  /** 极速权益 */
  SPEED = 'SPEED',
  /** 普通不购买权益 */
  NORMAL = 'NORMAL',
}

export interface H5BioCache {
  amount: string;
  maxAmount: string;
  period: PeriodOption;
  usage: LoanUsageOption;
  tradePasswdToken: string;
  channel: ApplyChannelData;
  bankCard: LoanBankCard;
  coupon: CouponRolePo;
  checkType: string;
  planListData: CalcPlanListData;
  checkId: string;
  repaymentModel: string;
  product: ApplyProductData | undefined;
  isVisibleLoading: boolean;
  selectRightInCapital: ProductCapitalItem | null;
  contracts: (ContractItem | RemoteContract | Commodity)[];
  totalAvailableAmount: number;
  h5BioLoanApplyMarketReq: LoanApplyMarketReqType | undefined;
}

export interface BindCardContext {
  from: string;
  back: string;
  commodityId: string;
  quickBindCardContext: Dictionary<string>;
}

export interface BindCardResult {
  bankCard: LoanBankCard;
}

/** 防诈骗配置 */
export interface AntiFraudConfigType {
  /** 顶部提示配置 */
  topTip: {
    /** 是否展示防诈顶部跑马灯 */
    tipShow: boolean,
    /** 顶部跑马灯展示文案 */
    tipText: string,
  },
  /** 提现是否需要防诈弹窗 */
  showFraudPopUp: boolean
}

export interface LoanRightStateType {
  /** 权益下单参数 */
  rightParams: RightSummaryType & {
    amount: string;
    period: string;
  } | null,
  /** 是否勾选了当前的臻享卡权益 */
  isRightTick: boolean;
  /** 权益卡片面板tab切换在极速模式 */
  showSpeed: boolean;
  /** 进入提现页权益第一次请求命中的倒计时时间 */
  recordCountDownTime: number;
  /** 折扣豁免倒计时是否到期 */
  isCountingDownFinished: boolean;
  /** 展示了超级会员的用户是否勾选了协议 */
  isSuperMemberCheck: boolean | null;
  /** 是否展示超级会员卡片 */
  showSuperMemberCard: boolean;
  /** 超级会员卡片信息 */
  superMemberInfo: SuperCardSummaryBackType | null;
}

export type getCalRepayPlans = {
  /** 还款方式 */
  repayType: string;
  /** 借款金额 */
  currentAmount: string;
  /** 是否需要重新请求权益规则 */
  notNeedRightRulerCheck?: boolean;
  /** 是否勾选权益 */
  equitySelected?: EquitySelectedEnum;
}

/** 自营b限额枚举 */
export enum QuoteLimiteEnum{
  /** 双承接模式：左24权益右纯24、20 */
  DUAL_CHANNEL = 'dualChannel',
  /** 右侧轻资产承接 */
  MIX_DUAL_CHANNEL = 'mixDualChannel',
  /** 双承接模式：左20权益右纯24、20 */
  DUAL_CHANNEL_20 = 'dualChannel_20',
}

/** 用户场景交互配置 */
export interface UserSceneConfig {
  /** 金额输入框是否禁用 */
  moneyAmountDisable: boolean;
  /** 金额提示是否隐藏 */
  moneyTipsHidden: boolean;
  /** 优惠券是否可取消 */
  couponCancelDisable: boolean;
}
